import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt } from 'react-icons/fa';
import { GiCancel } from 'react-icons/gi';
import { useStateContext } from '../../contexts/ContextProvider';
import axiosClient from '../../axios-client';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import TabHead from '../../components/TabHead';
import HourLoading from '../../components/HourLoading';

const EleveEmploie = ({ student, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [emploies, setEmploies] = useState([]);
  const [errors, setErrors] = useState(null);
  const { token, setToken } = useStateContext();
  const navigate = useNavigate();

  const getEmploies = async () => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get(`/eleveEmploie/${student.id}`);
      setEmploies(data.emploies);
      setErrors(null);
    } catch (err) {
      const response = err.response;
      if (response?.status === 401) {
        setToken(null);
      } else if (response?.status === 422) {
        setErrors(response.data.errors);
      } else if (response?.status === 403) {
        navigate('/403');
      } else if (response?.status === 404) {
        navigate('/404');
      } else {
        navigate('/500');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEmploies();
  }, [student.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 md:p-8 lg:p-10 animate-fade-in"
        role="dialog"
        aria-labelledby="student-emploie-title"
      >
        {/* Header */}
        <Header
          icon={<FaCalendarAlt className="text-blue-600" />}
          title={student.eleve}
          titleAr={student.eleve}
          className="mb-4"
        />

        {/* Errors */}
        {errors && (
          <div className="mb-4 space-y-2">
            {Object.keys(errors).map((key) => (
              <Alert key={key} message={errors[key][0]} />
            ))}
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mb-4">
          <Button
            icon={<GiCancel />}
            onClick={onClose}
            color="bg-red-500 hover:bg-red-600"
            textColor="text-white"
            text="Fermer"
            className="transition-colors duration-200"
          />
        </div>

        {/* Timetable */}
        <div className="mt-3 overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-4 py-3 text-left">
                  <TabHead left="Jour" right="اليوم" />
                </th>
                <th className="px-4 py-3 text-left">
                  <TabHead left="Matière" right="المادة" />
                </th>
                <th className="px-4 py-3 text-left">
                  <TabHead left="Heure" right="التوقيت" />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-10">
                    <HourLoading />
                  </td>
                </tr>
              ) : emploies.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-6 text-gray-500">
                    Aucun emploi du temps trouvé.
                  </td>
                </tr>
              ) : (
                emploies.map((emploi) => (
                  <tr
                    key={emploi.id}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-4 py-3">
                      <div className="flex justify-between">
                        <span>{emploi.jour.libelleFr}</span>
                        <span>{emploi.jour.libelleAr}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{emploi.matiere.matiere.libelle}</td>
                    <td className="px-4 py-3">
                      {emploi.heureDebut} - {emploi.heureFin}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
EleveEmploie.propTypes = {
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    eleve: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

// Default props
EleveEmploie.defaultProps = {
  student: {
    id: '',
    eleve: 'Unknown Student',
  },
};

export default EleveEmploie;